import * as React from "react";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { MobXProviderContext, observer } from "mobx-react";
import { QueryObject, RouteStore } from "bernie-core";
import { Navigation } from "src/stores/wizard/config";
import { FlexComponentNameToWizardStateConfig } from "../FlexComponentNameToWizardStateConfig";
import { useLocalization } from "@shared-ui/localization-context";
import { TabbedWizard } from "./TabbedWizard";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { Experiment as Experiments, isVariantEnabled } from "src/components/utility/experiment";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";

/**
 * @property lobForms: a list of React elements representing each LOB form added in the flex manager
 */
export interface LobFormWithTabsProps {
  context?: ExtendedContextStore;
  lobForms: React.ReactElement[];
  wizardFormClassNames?: string;
  tabGroupClassNames?: string;
  trackOnChangeTab?: (tabName: string) => void;
}

export enum PackageNamesTest {
  CONTROL = "wizard-package-pwa",
  CONTROL_TEXT = "Packages",
  VARIANT_1 = "wizard-package-pwa_variant_1",
}

export const LobFormWithTabs: React.FunctionComponent<LobFormWithTabsProps> = observer(
  ({ lobForms, wizardFormClassNames, tabGroupClassNames, trackOnChangeTab, context }) => {
    const keyHelperWizard = new ItemKeyHelper("storefrontForms");
    const keyHelperNavigation = new ItemKeyHelper("storefrontWizardNavigation");
    const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const defaultLobNavConfig: Navigation = {
      tabIcon: "help",
      tabTitleToken: "",
    };
    const { formatText } = useLocalization();

    //used by onTabSelect to update the query route
    const lobQueryCombiner = (prevQuery: QueryObject, nextQuery: QueryObject): QueryObject => {
      delete prevQuery.pwaSubLOB;
      delete prevQuery.packageSub;

      return { ...prevQuery, ...nextQuery };
    };

    //returns the localized string given the name of the flex component used
    const getLabel = (lobName: string): string => {
      let label = defaultLobNavConfig.tabTitleToken;
      if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
        label = formatText(wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabTitleToken);
      } else if (lobName === PackageNamesTest.VARIANT_1) {
        // remove this else if block when the experiment 38511 is completed
        label = formatText(
          wizardState[FlexComponentNameToWizardStateConfig[PackageNamesTest.CONTROL]].config.navigation
            .tabTitleVariantToken!
        );
      }

      return label;
    };

    const packagesVariantLabel = getLabel(PackageNamesTest.VARIANT_1);
    const isPackageTestVariantEnabled = context
      ? isVariantEnabled(context, Experiments.PWA_text_string_packages_test, 1)
      : false;

    const isPackageVariantActive = wizardState.activeLob === PackageNamesTest.CONTROL && isPackageTestVariantEnabled;
    const defaultLOB = isPackageVariantActive ? packagesVariantLabel : getLabel(wizardState.activeLob);
    //set the state to hold the default lob in the wizard store as the current value
    const [currentLOB, setCurrentLOB] = React.useState<string>(defaultLOB);

    /**
     * Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
     * CurrentLOB and defaultLOB set server side, but may be updated based on last search cookie value client side
     * To ensure up to date values on client side render, trigger an effect that does not have any dependent values to only run on first render
     */
    React.useEffect(() => {
      if (!routeStore.queryParams.pwaLob) {
        const { useDefaultLOB, defaultLob: pwaLob } = wizardState;

        if (useDefaultLOB) {
          routeStore.updateSearch({ pwaLob });
          wizardState.setUseDefaultLOB(false);
          setCurrentLOB(getLabel(pwaLob));
        } else {
          if (typeof window !== "undefined" && window.history) {
            window.history.back();
          }
        }
      }
    }, []);

    //need to be able to map the localized version of the lob form name to the flex component name
    const LobFormNames: { [label: string]: string } = {};
    lobForms.map((lob: React.ReactElement) => {
      const formName = lob.props.flexComponent.name;
      const formLabel = getLabel(formName);
      LobFormNames[formLabel] = formName;
    });

    const getFormNameFromLabel = (label: string): string => {
      if (label === packagesVariantLabel) {
        // Hacky way to make the string package deals match with the LobFormNames
        // when the test 38511 is bucket into 1. Remove this if block when the
        // test is completed.
        return LobFormNames[PackageNamesTest.CONTROL_TEXT];
      }

      return LobFormNames[label];
    };

    //on selecting a tab, update the wizard state with the flex component name of the current tab, and update the react state with the localized label that will be used on the react component
    //update the query route with the new pwaLob and pwaSubLob
    const onTabSelect = (tabSelected: any) => {
      const formName = getFormNameFromLabel(tabSelected);
      wizardState.setActiveLob(formName);
      setCurrentLOB(tabSelected);

      let queryParams: any = { pwaLob: formName };
      const { subLOBState } = wizardState.lobStateMap[formName];

      resetErrorsState();

      if (subLOBState) {
        queryParams = { pwaLob: formName, pwaSubLOB: subLOBState };
      }
      if (trackOnChangeTab) {
        trackOnChangeTab(formName);
      }
      routeStore.updateSearch(queryParams, {}, false, lobQueryCombiner);
    };

    const resetErrorsState = () => {
      wizardState.carWizardState.resetSubLobState();
      wizardState.carWizardState.resetSubLobTabs();
      wizardState.carWizardState.cleanErrorState();

      wizardState.lxWizardState.cleanErrorState();
      wizardState.hotelWizardState.cleanErrorState();
      wizardState.flightWizardState.cleanErrorState();
      wizardState.packageWizardState.cleanErrorState();
    };

    const lobIconsMap = {
      "wizard-hotel-pwa-v2": "lob_hotels",
      "wizard-flight-pwa": "lob_flights",
      "wizard-package-pwa": "lob_packages",
      "wizard-car-pwa": "lob_cars",
      "wizard-lx-pwa": "lob_activities",
      "wizard-cruise-pwa": "lob_cruise",
    };

    const renderTabs = (formattedName: string, lob: React.ReactElement) => {
      const lobName = lob.props.flexComponent.name;

      return (
        <UitkTab
          name={formattedName}
          targetURI={`?pwaLOB=${lobName}`}
          id={lobName}
          key={keyHelperNavigation.next()}
          iconKey={lobIconsMap[lobName]}
        >
          {(formattedName.includes("Package") || currentLOB === formattedName) && (
            <div className={wizardFormClassNames || ""}>
              <TabbedWizard key={keyHelperWizard.next()}>{lob}</TabbedWizard>
            </div>
          )}
        </UitkTab>
      );
    };

    return (
      <Experiment name={Experiments.PWA_text_string_packages_test}>
        <ExperimentControl>
          <UitkExperimentalButtonTabs
            onTabSelect={onTabSelect}
            selectedTab={currentLOB}
            tabsType={UitkTabsType.NATURAL}
            className={`lobNavigationFormWithTabs ${tabGroupClassNames ? tabGroupClassNames : ""}`}
            data-testid="lobNavigationFormWithTabs"
            tabAnchorType={UitkTabAnchorType.ICON_AND_TEXT}
          >
            {lobForms.map((lob: React.ReactElement) => {
              const lobName = lob.props.flexComponent.name;
              const formattedName = getLabel(lobName);

              return renderTabs(formattedName, lob);
            })}
          </UitkExperimentalButtonTabs>
        </ExperimentControl>
        <ExperimentVariant bucket={1}>
          <UitkExperimentalButtonTabs
            onTabSelect={onTabSelect}
            selectedTab={currentLOB}
            tabsType={UitkTabsType.NATURAL}
            className={`lobNavigationFormWithTabs ${tabGroupClassNames ? tabGroupClassNames : ""}`}
            data-testid="lobNavigationFormWithTabs"
            tabAnchorType={UitkTabAnchorType.ICON_AND_TEXT}
          >
            {lobForms.map((lob: React.ReactElement) => {
              const lobName = lob.props.flexComponent.name;
              const isPackage = lobName === PackageNamesTest.CONTROL;
              const formattedName = isPackage ? getLabel(PackageNamesTest.VARIANT_1) : getLabel(lobName);

              return renderTabs(formattedName, lob);
            })}
          </UitkExperimentalButtonTabs>
        </ExperimentVariant>
      </Experiment>
    );
  }
);
